/**
 * Reading one change-log entry (src/features/system/changes/NNNN-slug.md).
 *
 * Plain logic, no framework, so the three places that read the change log all
 * read it the same way: the System page (bundled by Vite), the server's build
 * manifest (read from disk, docs/ENVIRONMENTS.md) and `pnpm promote` (read out
 * of git). The parsing is a dozen lines of regex rather than a markdown
 * dependency: these files are a heading, a paragraph and some bullets.
 */

import { type ChangeTour, parseChangeTour } from './change-tour.ts'

/** The repository these link into. */
export const CHANGE_LOG_REPO = 'BuildsByGideon1/majik-crm'

/** Where the entries live, relative to the project root. */
export const CHANGE_LOG_DIR = 'src/features/system/changes'

/** `0057-sandbox-before-live.md` — a number, a slug, `.md`. */
export const CHANGE_FILE = /^(\d+)-[a-z0-9-]+\.md$/

export interface ChangeEntry {
  /** The leading number in the filename — the order things shipped. */
  order: number
  slug: string
  title: string
  date: string
  /**
   * When it reached production, from Railway's deployment history
   * (`pnpm changes:deployed`). ISO, UTC. Absent for entries that went live
   * before deploys were tracked by commit.
   */
  deployed?: string
  pr?: number
  commit?: string
  /**
   * The commit its video is filmed at, when `commit` can't show the change —
   * e.g. the sample data or the renderer that films it landed later
   * (docs/SYSTEM.md → The video job). Absent: filmed at `commit`.
   */
  filmAt?: string
  /** The opening paragraph. */
  summary: string
  bullets: string[]
  /** Where to read the actual diff, when there is somewhere to point. */
  url?: string
  /**
   * The "Show me" steps on the test site, or `'none'` when nothing on screen
   * changed (./change-tour.ts). Required from TOUR_REQUIRED_FROM on.
   */
  tour?: ChangeTour
  /** What is wrong with a `tour:` that is there but malformed — changes.test.ts names it. */
  tourProblem?: string
}

/** The file name without `.md` — what the build manifest lists. */
export function changeSlug(fileName: string): string {
  return fileName.replace(/\.md$/, '')
}

export function parseChangeEntry(fileName: string, raw: string): ChangeEntry | null {
  const name = fileName.split('/').pop() ?? ''
  if (name.toLowerCase() === 'readme.md') return null

  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/.exec(raw)
  if (!match) return null
  const [, frontmatter = '', body = ''] = match

  const field = (key: string): string | undefined =>
    new RegExp(`^${key}:\\s*(.+?)\\s*(?:#.*)?$`, 'm').exec(frontmatter)?.[1]?.trim()

  const title = field('title')
  const date = field('date')
  if (!title || !date) return null

  const lines = body.trim().split(/\r?\n/)
  const firstBullet = lines.findIndex((line) => line.trimStart().startsWith('- '))
  const prose = firstBullet === -1 ? lines : lines.slice(0, firstBullet)
  const summary = prose
    .filter((line) => line.trim().length > 0)
    .join(' ')
    .trim()

  // A bullet may wrap over several lines. Continuation lines belong to the
  // bullet above, not to the paragraph — getting this wrong silently moved
  // half a sentence into the summary.
  const bullets: string[] = []
  for (const line of firstBullet === -1 ? [] : lines.slice(firstBullet)) {
    const trimmed = line.trim()
    if (trimmed.startsWith('- ')) bullets.push(trimmed.slice(2).trim())
    else if (trimmed.length > 0 && bullets.length > 0) {
      bullets[bullets.length - 1] = `${bullets.at(-1)} ${trimmed}`
    }
  }

  const pr = field('pr') ? Number(field('pr')) : undefined
  const commit = field('commit')
  const filmAt = field('filmAt')
  const deployed = field('deployed')
  const slug = changeSlug(name)
  const tour = parseChangeTour(frontmatter)

  return {
    order: Number(/^(\d+)/.exec(name)?.[1] ?? 0),
    slug,
    title,
    date,
    deployed,
    pr,
    commit,
    filmAt,
    summary,
    bullets,
    url: pr
      ? `https://github.com/${CHANGE_LOG_REPO}/pull/${pr}`
      : commit
        ? `https://github.com/${CHANGE_LOG_REPO}/commit/${commit}`
        : undefined,
    tour: tour.tour,
    tourProblem: tour.problem,
  }
}

/** Newest first — the question is almost always "what changed recently". */
export function newestFirst(entries: ChangeEntry[]): ChangeEntry[] {
  return [...entries].sort((a, b) => b.order - a.order)
}
